import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCallback, useEffect, useRef } from 'react';

const CACHE_KEY = 'exchange_rates_cache';
const CACHE_MAX_AGE = 60 * 60 * 1000; // 1 hour in ms

interface CachedRates {
  rates: Record<string, number>;
  timestamp: number;
}

interface ExchangeRatesResponse {
  success: boolean;
  rates: Record<string, number>;
  cached?: boolean;
  stale?: boolean;
  timestamp?: number;
  error?: string;
}

function getCachedRates(): Record<string, number> | undefined {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return undefined;
    
    const parsed: CachedRates = JSON.parse(cached);
    const age = Date.now() - parsed.timestamp;
    
    // Return cached rates if less than max age
    if (age < CACHE_MAX_AGE && parsed.rates) {
      return parsed.rates;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function setCachedRates(rates: Record<string, number>) {
  try {
    const cache: CachedRates = {
      rates,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore localStorage errors
  }
}

// Get initial cached rates once at module level to avoid hook ordering issues
const initialCachedRates = getCachedRates();

export function useExchangeRates() {
  // Use ref to track if we've saved to cache to avoid repeated writes
  const hasSavedToCache = useRef(false);
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['exchange-rates'],
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await supabase.functions.invoke<ExchangeRatesResponse>('exchange-rates');
      
      if (error) {
        console.error('Failed to fetch exchange rates:', error);
        throw error;
      }
      
      if (!data?.success || !data?.rates) {
        throw new Error(data?.error || 'Invalid exchange rates response');
      }
      
      return data.rates;
    },
    initialData: initialCachedRates,
    staleTime: 30 * 60 * 1000, // Consider fresh for 30 minutes
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Cache rates to localStorage when fresh data arrives
  useEffect(() => {
    if (data && !hasSavedToCache.current && data !== initialCachedRates) {
      setCachedRates(data);
      hasSavedToCache.current = true;
    }
  }, [data]);

  const rates = data || null;

  /**
   * Convert a price from one currency to another
   * Uses USD as the base currency for conversion
   */
  const convertPrice = useCallback((
    amount: number,
    fromCurrency: string,
    toCurrency: string
  ): number | null => {
    if (!rates) return null;
    if (fromCurrency === toCurrency) return amount;
    
    const fromRate = rates[fromCurrency.toUpperCase()];
    const toRate = rates[toCurrency.toUpperCase()];
    
    if (!fromRate || !toRate) {
      console.warn(`Missing exchange rate for ${fromCurrency} or ${toCurrency}`);
      return null;
    }
    
    // Convert: amount in fromCurrency -> USD -> toCurrency
    // amount / fromRate gives USD value, then * toRate gives toCurrency value
    const converted = (amount / fromRate) * toRate;
    
    return converted;
  }, [rates]);

  /**
   * Check if we can convert between two currencies
   */
  const canConvert = useCallback((
    fromCurrency: string,
    toCurrency: string
  ): boolean => {
    if (!rates) return false;
    return !!(rates[fromCurrency.toUpperCase()] && rates[toCurrency.toUpperCase()]);
  }, [rates]);

  // Only show loading if we have NO data at all (no cache, no fetch complete)
  // When we have initialData from cache, don't block rendering
  const effectiveLoading = isLoading && !data;

  return {
    rates,
    isLoading: effectiveLoading,
    error,
    convertPrice,
    canConvert,
  };
}
